import React from 'react';

import AdminApi from '../admin/AdminApi';
import UnityHandler from '../../designer/UnityHandler';
import PrestaGlobals from '../common/PrestaGlobals';
import { scriptDesignName } from '../common/NameUtils';
import { keepShownDesign, shownDesign } from '../common/ShownDesign';
import { DatabaseContext } from '../database/DatabaseHandler';
import { ParametersContext } from '../parametersProcessor/ParametersProcessor';
import { CartContext } from '../cart/CartHandler';
import IUnityTransport from '../unityView/transport/IUnityTransport';
import { isUnityEditorModeEnabled, UNITY_EDITOR_URL } from '../unityView/transport/unityEditorMode';
import DesignerDriver, { IBridgeCall } from './designerDriver';

/**
 * State of the AI designer page (/ai-designer), kept above its panel: a design keeps running
 * while the admin switches between the panel's tabs.
 *
 * The AI designer service (Unity project, designer-agent/) runs the uber-llm-designer skill. This
 * provider opens its WebSocket with a token from `ucadmin&action=aiDesignerSession`, sends the
 * admin's brief, turns the service's events into the feed, and answers the service's Designer calls
 * through DesignerDriver against this page's Unity. It also tracks which ucon + StyleScript pair
 * the Designer shows, for the Szkriptek tab and for a brief that builds on what is loaded.
 *
 * Conversations last on the service, as the AR app's do (designer-agent/README.md, "Coming back to
 * a conversation"): a dropped socket reconnects and gets what it missed, a reload or a later visit
 * opens the conversation again (`?conversation=<id>`, or the last one this tab had), and the
 * list of earlier ones opens any of the shop's. Every conversation designs for a room: the service
 * keeps each verified design as a revision of it, the page steps through them and follows the
 * room's new ones (the AR app's too) over `/rooms/ws`; `?room=<id>` opens a room.
 */

export interface IImageInput {
    name: string;
    mediaType: string;
    /** base64, no data: prefix */
    data: string;
    /** a data URL for the thumbnails */
    preview: string;
}

export interface IShot { path: string; data: string; }

export type TFeedItem =
    | { kind: 'user'; key: number; text: string; images: string[] }
    | { kind: 'assistant'; key: number; text: string }
    | { kind: 'thinking'; key: number; text: string }
    | {
        kind: 'tool'; key: number; id: string; name: string; input: string;
        state: 'running' | 'ok' | 'error'; output?: string; exit?: number; images: IShot[];
    }
    | { kind: 'notice'; key: number; text: string; level: 'info' | 'error' };

export interface IDesignFile { path: string; text: string | null; }
export interface IDesign { ucon: IDesignFile; style: IDesignFile; ok: boolean; }
export interface IUsage {
    input: number; output: number; cache_read: number; cache_write: number; cost: number | null; model: string;
}
/**
 * `retrying`: the socket dropped while there is a conversation; it reconnects on its own.
 * `taken`: another window or the AR app opened the conversation and took it over; this page takes it
 * back only when asked (`takeBack`, or the next message).
 */
export type TConnection = 'idle' | 'connecting' | 'open' | 'closed' | 'retrying' | 'taken';

/** The service's close code for a socket whose conversation another client took over. */
const TAKEN_OVER = 4000;
/** A model the service lets a conversation run on; price in USD per million tokens (input, output, cache read, cache write). */
export interface IModelChoice { id: string; label: string; price: number[] | null; }
/** What the service offers (its /health and hello): the defaults, and every model and effort allowed. */
export interface IChoices { model: string; effort: string; models: IModelChoice[]; efforts: string[]; }
/** The admin's pick for a new conversation; left out, the service's defaults. */
export interface IRunOptions { model?: string; effort?: string; }
export interface IActivity { state: 'idle' | 'thinking' | 'writing' | 'tool' | 'designer'; detail?: string; }
/**
 * The ucon + StyleScript pair the Designer shows, as text, and who loaded it last: the product's
 * own scripts, the AI designer's last verify, or an edit from the Szkriptek tab.
 */
/**
 * `floorplan`: for a room made from a room scan, the service's account of the scan (walls, openings, objects).
 * `revision`: the room's revision this pair is, when it is one.
 */
export interface IScriptPair {
    ucon: string; style: string | null; source: 'product' | 'ai' | 'manual' | 'scan' | 'revision';
    floorplan?: unknown; revision?: number;
}

/** A room scan turned into a room: the service's report lines, or why it could not. */
export type TScanResult = { ok: true; report: string[] } | { ok: false; error: string };

/** the service's limit for a room scan (.usdz) */
export const MAX_SCAN_BYTES = 25 * 1024 * 1024;

/** A revision of a room, without its scripts (designer-agent/designer_agent/rooms.py). */
export interface IRevision {
    n: number;
    source: 'app-agent' | 'page-agent' | 'app-manual' | 'page-manual';
    kind: 'iteration' | 'final';
    note?: string | null;
    agent_run?: string | null;
    created_at: string;
}
/** A room of the shop: a scanned room, or a design project (`has_room`: it has a scan). */
export interface IRoom {
    id: string; label: string; created_at: string; updated_at: string; current_revision: number;
    has_room?: boolean; scan?: string | null;
}
/** A conversation as the service lists it (its log/conversation.json). */
export interface IConversation {
    id: string; room: string | null; title: string; model: string; effort: string; client: 'page' | 'app';
    employee: number | null; created_at: string; updated_at: string; turns: number; status: 'idle' | 'working';
    revisions: number[]; cost?: number | null; live: boolean; busy: boolean; client_on: boolean;
}

interface IState {
    connection: TConnection;
    /** seconds to the next reconnect, while the socket is down and the conversation goes on */
    retryIn: number | null;
    model: string | null;
    effort: string | null;
    /** null until the service has said what it offers */
    choices: IChoices | null;
    /** the conversation's id on the service (the `session` event's) */
    conversation: string | null;
    title: string | null;
    /** the last event number seen: a reconnect asks for those after it */
    lastSeq: number;
    /** events the service still replays after a `resumed` */
    replaying: number;
    busy: boolean;
    activity: IActivity;
    feed: TFeedItem[];
    /** the pair the last verify loaded into the Designer */
    design: IDesign | null;
    files: IDesignFile[];
    usage: IUsage | null;
    /** the room the conversation designs for (or the one opened for the next) */
    room: string | null;
    roomLabel: string | null;
    revisions: IRevision[];
    /** the revision the agent's last verify became */
    agentRevision: number | null;
}

const initialState: IState = {
    connection: 'idle', retryIn: null, model: null, effort: null, choices: null, conversation: null, title: null,
    lastSeq: 0, replaying: 0, busy: false, activity: { state: 'idle' }, feed: [], design: null, files: [], usage: null,
    room: null, roomLabel: null, revisions: [], agentRevision: null,
};

type TAction =
    | { type: 'connection'; connection: TConnection; retryIn?: number | null }
    | { type: 'user'; text: string; images: string[]; fresh: boolean }
    | { type: 'notice'; text: string; level: 'info' | 'error'; idle?: boolean }
    | { type: 'reset'; keepRoom: boolean }
    | { type: 'open'; conversation: string }
    | { type: 'room'; room: string | null; label?: string | null; revisions?: IRevision[] }
    | { type: 'revisions'; room: string; revisions: IRevision[] }
    | { type: 'event'; event: any };

let nextKey = 1;

function appendText(feed: TFeedItem[], kind: 'assistant' | 'thinking', delta: string): TFeedItem[] {
    const last = feed[feed.length - 1];
    if (last && last.kind === kind) {
        return [...feed.slice(0, -1), { ...last, text: last.text + delta }];
    }
    return [...feed, { kind, key: nextKey++, text: delta }];
}

/** A fresh conversation's state: what the service offers stays, and the room when asked. */
function fresh(state: IState, keepRoom: boolean): IState {
    return {
        // a conversation taken over elsewhere is that one's state, not the next one's
        ...initialState, connection: state.connection === 'taken' ? 'idle' : state.connection,
        model: state.model, effort: state.effort, choices: state.choices,
        ...(keepRoom ? { room: state.room, roomLabel: state.roomLabel, revisions: state.revisions } : {}),
    };
}

function reducer(state: IState, action: TAction): IState {
    switch (action.type) {
        case 'connection':
            return { ...state, connection: action.connection, retryIn: action.retryIn ?? null };
        case 'reset':
            return fresh(state, action.keepRoom);
        case 'open':
            // the service replays the conversation from its first event
            return { ...fresh(state, false), conversation: action.conversation };
        case 'room':
            return action.room === state.room
                ? { ...state, roomLabel: action.label ?? state.roomLabel, revisions: action.revisions ?? state.revisions }
                : { ...state, room: action.room, roomLabel: action.label ?? null, revisions: action.revisions ?? [] };
        case 'revisions':
            return action.room === state.room ? { ...state, revisions: action.revisions } : state;
        case 'user': {
            const base = action.fresh ? fresh(state, true) : state;
            return {
                ...base, busy: true, activity: { state: 'thinking' },
                feed: [...base.feed, { kind: 'user', key: nextKey++, text: action.text, images: action.images }],
            };
        }
        case 'notice':
            return {
                ...state,
                ...(action.idle ? { busy: false, activity: { state: 'idle' } } : {}),
                feed: [...state.feed, { kind: 'notice', key: nextKey++, text: action.text, level: action.level }],
            };
        case 'event': {
            const e = action.event;
            if (typeof e?.seq !== 'number') return applyEvent(state, e);
            if (e.seq <= state.lastSeq) return state;     // had it before the socket dropped
            const replayed = state.replaying > 0;
            const next = { ...state, lastSeq: e.seq, replaying: Math.max(0, state.replaying - 1) };
            if (e.type === 'admin') {
                // the user's message: this page shows what it sent itself; a replay brings the rest
                if (!replayed) return next;
                const attachments: string[] = Array.isArray(e.attachments) ? e.attachments : [];
                const text = [String(e.text ?? ''), attachments.length ? `[${attachments.join(', ')}]` : '']
                    .filter(Boolean).join('\n');
                return { ...next, feed: [...next.feed, { kind: 'user', key: nextKey++, text, images: [] }] };
            }
            return applyEvent(next, e);
        }
    }
}

function applyEvent(state: IState, e: any): IState {
    switch (e.type) {
        case 'hello':
            return {
                ...state,
                choices: Array.isArray(e.models) && e.models.length
                    ? { model: e.model, effort: e.effort, models: e.models, efforts: e.efforts ?? [] }
                    : state.choices,
            };
        case 'session':
            return {
                ...state, conversation: e.id, model: e.model ?? state.model, effort: e.effort ?? state.effort,
                ...(e.room && e.room !== state.room ? { room: e.room, roomLabel: null, revisions: [] } : {}),
            };
        case 'resumed':
            return {
                ...state, busy: !!e.busy, activity: e.busy ? state.activity : { state: 'idle' },
                replaying: e.replayed ?? 0, model: e.model ?? state.model, effort: e.effort ?? state.effort,
                title: e.title ?? state.title,
                ...(e.room && e.room !== state.room ? { room: e.room, roomLabel: null, revisions: [] } : {}),
            };
        case 'status':
            return e.state === 'idle'
                ? { ...state, busy: false, activity: { state: 'idle' } }
                : { ...state, activity: { state: e.state, detail: e.detail } };
        case 'text':
            return { ...state, feed: appendText(state.feed, 'assistant', String(e.delta ?? '')) };
        case 'thinking':
            return { ...state, feed: appendText(state.feed, 'thinking', String(e.delta ?? '')) };
        case 'tool_start':
            return {
                ...state,
                feed: [...state.feed, {
                    kind: 'tool', key: nextKey++, id: e.id, name: e.name, input: e.input, state: 'running', images: [],
                }],
            };
        case 'tool_end':
            return {
                ...state,
                design: e.design ?? state.design,
                agentRevision: typeof e.revision === 'number' ? e.revision : state.agentRevision,
                feed: state.feed.map((item) => item.kind === 'tool' && item.id === e.id
                    ? { ...item, state: e.ok ? 'ok' : 'error', output: e.output, exit: e.exit, images: e.images ?? [] }
                    : item),
            };
        case 'usage':
            return { ...state, usage: e };
        case 'notice':
            return { ...state, feed: [...state.feed, { kind: 'notice', key: nextKey++, text: e.text, level: 'info' }] };
        case 'error':
            return { ...state, feed: [...state.feed, { kind: 'notice', key: nextKey++, text: e.message, level: 'error' }] };
        case 'done':
            return { ...state, busy: false, activity: { state: 'idle' }, files: e.files ?? state.files };
        default:
            return state;
    }
}

interface IAiDesignerContext extends IState {
    /** The pair the Designer shows now; null until the product's scripts have loaded. */
    current: IScriptPair | null;
    /**
     * A new conversation: the brief, its images, optionally the scripts the Designer shows now, and
     * the model to run on. It designs for the room open now, or for a new one the service makes.
     */
    start: (prompt: string, images: IImageInput[], includeCurrent: boolean, options?: IRunOptions) => Promise<void>;
    /** Asks the service what it offers (models, efforts), for the choice before a conversation starts. */
    loadChoices: () => Promise<void>;
    /** An answer or a change request in the running conversation. */
    send: (text: string, images: IImageInput[]) => Promise<void>;
    stop: () => void;
    /** Takes the conversation back from the window or app that took it over. */
    takeBack: () => void;
    /** Ready for a new conversation, in the same room. */
    reset: () => void;
    /** Ready for a new conversation in a new room (a new design project). */
    newRoom: () => void;
    /** Loads the last verified design into the Designer again. */
    applyDesign: () => void;
    /** Loads an edited pair into the Designer; false while Unity is not there yet. */
    applyScripts: (ucon: string, style: string | null) => boolean;
    /** Loads the product's own pair into the Designer again; false while Unity or the product's scripts are not there yet. */
    restoreProduct: () => boolean;
    /**
     * Turns a room scan (.usdz, from an iPhone or iPad) into a room - the service runs the AI
     * designer's floorplan.py and keeps the room - and loads it into the Designer, so the admin
     * checks it before a design starts in it with "A látható tervből indulj ki".
     */
    loadScan: (file: File) => Promise<TScanResult>;
    /** Takes an earlier conversation up again: its feed is replayed, and it can go on. */
    openConversation: (id: string) => void;
    /** Opens a room: its latest revision in the Designer, the next conversation designs for it. */
    openRoom: (room: string) => void;
    /** The next conversation designs for a new room. */
    leaveRoom: () => void;
    /** Loads revision n of the room into the Designer. */
    showRevision: (n: number, whenReady?: boolean) => Promise<boolean>;
    /** Keeps what the Designer shows as a new revision of the room (an edit made by hand). */
    saveRevision: (note?: string) => Promise<boolean>;
    /** The shop's conversations (of one room), the latest first; null when the service does not answer. */
    listConversations: (room?: string) => Promise<IConversation[] | null>;
    /** The shop's rooms, the latest changed first; null when the service does not answer. */
    listRooms: () => Promise<IRoom[] | null>;
    renameRoom: (label: string) => Promise<boolean>;
}

const noop = () => {};
export const AiDesignerContext = React.createContext<IAiDesignerContext>({
    ...initialState,
    current: null,
    start: async () => {},
    loadChoices: async () => {},
    send: async () => {},
    stop: noop,
    takeBack: noop,
    reset: noop,
    newRoom: noop,
    applyDesign: noop,
    applyScripts: () => false,
    restoreProduct: () => false,
    loadScan: async () => ({ ok: false, error: '' }),
    openConversation: noop,
    openRoom: noop,
    leaveRoom: noop,
    showRevision: async () => false,
    saveRevision: async () => false,
    listConversations: async () => null,
    listRooms: async () => null,
    renameRoom: async () => false,
});

/** The StyleScript UnityView sends for the product's parameters; null until there are any. */
function productStyle(params: React.ContextType<typeof ParametersContext>): string | null {
    if (Object.keys(params.parameters).length === 0) return null;
    try {
        return JSON.stringify(params.generateStyleScript(), null, 1);
    } catch {
        return null;
    }
}

/** Seconds between reconnects while the socket is down: 1, 2, 4, 8, 15, then every 30. */
const BACKOFF = [1, 2, 4, 8, 15, 30];
/** Answers to Designer calls are sent again after a reconnect for this long (the old socket may have lost them). */
const RESEND_MS = 60_000;
/** The conversation this tab had last (sessionStorage: every tab has its own, so two tabs design two
 * things side by side, and a new tab starts afresh): a reload opens it again. */
const LAST_KEY = 'ucAiDesigner.conversation';

function remember(conversation: string | null) {
    try {
        if (conversation) sessionStorage.setItem(LAST_KEY, conversation);
        else sessionStorage.removeItem(LAST_KEY);
    } catch {
        // no storage (a private window): a reload starts afresh
    }
}

function remembered(): string | null {
    try {
        return sessionStorage.getItem(LAST_KEY);
    } catch {
        return null;
    }
}

interface IService { base: string; token: string | null; expires: number; }

/** `loaded`: Unity has answered SetShop (UCOnLoadedReceived) - its catalog is there to build with. */
const AiDesignerProvider: React.FC<{ loaded: boolean; children?: React.ReactNode }> = ({ loaded, children }) => {
    const data = React.useContext(DatabaseContext);
    const params = React.useContext(ParametersContext);
    const cart = React.useContext(CartContext);
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const [current, setCurrent] = React.useState<IScriptPair | null>(null);
    const currentRef = React.useRef(current);
    currentRef.current = current;
    const stateRef = React.useRef(state);
    stateRef.current = state;
    /** the conversation this tab had when the page opened (read before it is kept up to date) */
    const lastAtStart = React.useRef(remembered());
    /** the pair a page of this tab showed last (ShownDesign), when the page opened */
    const shownAtStart = React.useRef(shownDesign());
    /** the last conversation, opened again with the page: the pair kept from the other page stays over its design */
    const keptOver = React.useRef<string | null>(null);

    // The product's pair, as UnityView sends it: the ucon whenever it loads, the StyleScript once
    // there are parameters. A product switch or a cart item opened for editing loads a new one.
    React.useEffect(() => {
        if (!data.uconScript) return;
        setCurrent({ ucon: JSON.stringify(data.uconScript, null, 2), style: productStyle(params), source: 'product' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.uconScript, params.parameters]);

    // Each verify loads the AI designer's pair into the Designer.
    React.useEffect(() => {
        if (state.design?.ucon.text) {
            setCurrent({ ucon: state.design.ucon.text, style: state.design.style.text, source: 'ai',
                ...(stateRef.current.agentRevision ? { revision: stateRef.current.agentRevision } : {}) });
        }
    }, [state.design]);

    // "Kosárba" stores what the Designer shows: a design's ucon goes to the cart in place of the
    // product's (CartHandler.setDesignUcon), with the order Unity built from it.
    // The line is named by the pair (scriptDesignName), not by the loaded product.
    const design = React.useMemo(() => {
        if (!current || current.source === 'product') return null;
        let ucon: any;
        try {
            ucon = JSON.parse(current.ucon);
        } catch {
            return null;
        }
        let style: unknown = null;
        try {
            style = current.style ? JSON.parse(current.style) : null;
        } catch {
            // the ucon's own name is still there
        }
        return { ucon, name: scriptDesignName(ucon, style) };
    }, [current]);
    const designUcon = design?.ucon ?? null;
    React.useEffect(() => {
        cart.setDesignUcon(design?.ucon ?? null, design?.name ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [design]);
    React.useEffect(() => () => cart.setDesignUcon(null), []);
    // UnityView prices the configuration after its own StyleScript only; a design's price comes
    // with its order. Declared after setDesignUcon, so the price is the design's.
    React.useEffect(() => {
        if (designUcon && cart.cabinetResult) cart.getQuickAndDirtyPrice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [designUcon, cart.cabinetResult]);

    const socket = React.useRef<WebSocket | null>(null);
    const connecting = React.useRef<Promise<WebSocket> | null>(null);
    const loadedRef = React.useRef(loaded);
    loadedRef.current = loaded;
    /** answers to Designer calls, for sending again after a reconnect */
    const answers = React.useRef<{ json: string; at: number }[]>([]);
    const retry = React.useRef<{ timer: number | null; attempt: number }>({ timer: null, attempt: 0 });
    /** an opened conversation's last design goes into the Designer once its replay is over */
    const reopened = React.useRef(false);
    const replayPending = React.useRef(false);

    const driver = React.useMemo(() => new DesignerDriver({
        instance: () => UnityHandler.instance as unknown as IUnityTransport | null,
        loaded: () => loadedRef.current,
        editorUrl: PrestaGlobals.isAdmin && isUnityEditorModeEnabled() ? UNITY_EDITOR_URL : null,
        canvas: () => document.querySelector<HTMLCanvasElement>('#unity-canvas'),
    }), []);

    // --- the service's address and token (a fresh one when the last is about to expire) ---------------
    const serviceCache = React.useRef<IService | null>(null);
    const service = React.useCallback(async (): Promise<IService> => {
        const cached = serviceCache.current;
        if (cached && cached.expires - 30 > Date.now() / 1000) return cached;
        const session = await AdminApi.aiDesignerSession();
        if (!session || 'error' in session) {
            throw new Error(session?.error ?? 'A szerver nem adott munkamenetet az AI tervezőhöz.');
        }
        const fresh = { base: session.url.replace(/\/+$/, ''), token: session.token, expires: session.expires };
        serviceCache.current = fresh;
        return fresh;
    }, []);

    const withToken = (url: string, token: string | null) =>
        token ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;

    /** A JSON route of the service: its answer, or null when it did not give one (`ok: false` included). */
    const call = React.useCallback(async <T, >(path: string, init?: RequestInit): Promise<T | null> => {
        try {
            const { base, token } = await service();
            const response = await fetch(withToken(`${base}${path}`, token), init);
            const answer = await response.json();
            return answer?.ok ? answer as T : null;
        } catch {
            return null;
        }
    }, [service]);

    // --- the conversation's socket -----------------------------------------------------------------
    const send = React.useCallback((json: string) => {
        const ws = socket.current;
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(json);
    }, []);

    const onMessage = React.useCallback((raw: string) => {
        let message: any;
        try {
            message = JSON.parse(raw);
        } catch {
            return;
        }
        if (message?.type === 'bridge') {
            if (typeof message.seq === 'number' && message.seq <= stateRef.current.lastSeq) return;
            driver.run(message as IBridgeCall).then((answer) => {
                const json = JSON.stringify({ type: 'bridge_reply', id: message.id, ...answer });
                answers.current = [...answers.current.filter((a) => Date.now() - a.at < RESEND_MS), { json, at: Date.now() }];
                send(json);
            });
            // the call still counts as seen: a reconnect asks for what came after it
            dispatch({ type: 'event', event: { type: 'bridge-seen', seq: message.seq } });
            return;
        }
        if (message?.type === 'resumed') {
            retry.current.attempt = 0;
            // answers the old socket may have lost; the service drops those it no longer waits for
            answers.current.filter((a) => Date.now() - a.at < RESEND_MS).forEach((a) => send(a.json));
            replayPending.current = true;
        }
        dispatch({ type: 'event', event: message });
    }, [driver, send]);

    const scheduleReconnect = React.useRef<() => void>(noop);

    /** The open socket, or a new one: to the conversation (	arget, else the one in the state) after
     * the events it has, or for a new conversation when there is none. */
    const connect = React.useCallback((target?: { conversation: string; after: number }): Promise<WebSocket> => {
        if (socket.current && socket.current.readyState === WebSocket.OPEN) {
            return Promise.resolve(socket.current);
        }
        if (connecting.current) {
            return connecting.current;
        }
        const conversation = target ? target.conversation : stateRef.current.conversation;
        const after = target ? target.after : stateRef.current.lastSeq;
        dispatch({ type: 'connection', connection: 'connecting' });
        const attempt = (async () => {
            const { base, token } = await service();
            let url = withToken(`${base.replace(/^http/, 'ws')}/ws`, token);
            if (conversation) {
                url += `${url.includes('?') ? '&' : '?'}session=${encodeURIComponent(conversation)}&after=${after}`;
            }
            return new Promise<WebSocket>((resolve, reject) => {
                const ws = new WebSocket(url);
                let opened = false;
                ws.onopen = () => {
                    opened = true;
                    socket.current = ws;
                    dispatch({ type: 'connection', connection: 'open' });
                    resolve(ws);
                };
                ws.onmessage = (event) => { if (socket.current === ws) onMessage(String(event.data)); };
                ws.onclose = (event) => {
                    const mine = socket.current === ws;
                    if (mine) socket.current = null;
                    if (!opened) {
                        reject(new Error(`Nem sikerült kapcsolódni az AI tervezőhöz (${base}). `
                            + 'Fut a szolgáltatás, és egyezik a titkos kulcs a bolt .env fájljával?'));
                    } else if (mine && event.code === TAKEN_OVER) {
                        // another window or the app has it now: reconnecting would take it back, and that
                        // one would take it back again
                        dispatch({ type: 'connection', connection: 'taken' });
                        dispatch({ type: 'notice', level: 'info', idle: true,
                            text: 'Ezt a beszélgetést most máshol nyitották meg (másik ablakban vagy a telefonon). '
                                + 'A „Folytatás itt” vagy egy új üzenet visszahozza ide.' });
                    } else if (mine) {
                        // dropped (or closed by the service): the conversation goes on there
                        scheduleReconnect.current();
                    }
                };
            });
        })();
        connecting.current = attempt;
        attempt.catch(() => dispatch({ type: 'connection', connection: 'closed' }))
            .finally(() => { connecting.current = null; });
        return attempt;
    }, [onMessage, service]);

    scheduleReconnect.current = () => {
        const r = retry.current;
        if (r.timer !== null || !stateRef.current.conversation) {
            if (!stateRef.current.conversation) dispatch({ type: 'connection', connection: 'closed' });
            return;
        }
        const wait = BACKOFF[Math.min(r.attempt, BACKOFF.length - 1)];
        r.attempt += 1;
        dispatch({ type: 'connection', connection: 'retrying', retryIn: wait });
        r.timer = window.setTimeout(async () => {
            r.timer = null;
            try {
                await connect();
            } catch {
                // the service answers, but not this conversation (it is another shop's, or gone): give up
                const health = await fetch(`${(await service().catch(() => null))?.base ?? ''}/health`).then((x) => x.ok).catch(() => false);
                if (health && r.attempt >= 2) {
                    r.attempt = 0;
                    remember(null);
                    dispatch({ type: 'notice', level: 'error', idle: true,
                        text: 'Ez a beszélgetés nem folytatható (a szolgáltatás nem ismeri). Új tervezés indul a következő üzenettel.' });
                    dispatch({ type: 'reset', keepRoom: true });
                    dispatch({ type: 'connection', connection: 'closed' });
                    return;
                }
                scheduleReconnect.current();
            }
        }, wait * 1000);
    };

    /** Closes the socket on purpose: no reconnect. A running turn goes on on the service. */
    const disconnect = React.useCallback(() => {
        const r = retry.current;
        if (r.timer !== null) window.clearTimeout(r.timer);
        r.timer = null;
        r.attempt = 0;
        const ws = socket.current;
        socket.current = null;
        ws?.close();
        dispatch({ type: 'connection', connection: 'idle' });
    }, []);

    // Leaving the page closes the socket (no reconnect: it is no longer the provider's); the
    // conversation stays on the service and opens again.
    React.useEffect(() => () => {
        const r = retry.current;
        if (r.timer !== null) window.clearTimeout(r.timer);
        r.timer = null;
        const ws = socket.current;
        socket.current = null;
        ws?.close();
    }, []);

    const remembering = React.useRef(false);
    React.useEffect(() => {
        // not before the page has opened what it was linked to (or the last conversation)
        if (remembering.current || state.conversation) {
            remembering.current = true;
            remember(state.conversation);
        }
    }, [state.conversation]);

    const deliver = React.useCallback(async (message: Record<string, unknown>) => {
        try {
            const ws = await connect();
            ws.send(JSON.stringify(message));
        } catch (error) {
            dispatch({ type: 'notice', level: 'error', idle: true,
                text: error instanceof Error ? error.message : String(error) });
        }
    }, [connect]);

    const plain = (images: IImageInput[]) => images.map(({ name, mediaType, data: base64 }) => ({ name, mediaType, data: base64 }));

    const choicesRef = React.useRef(state.choices);
    choicesRef.current = state.choices;
    const loadChoices = React.useCallback(async () => {
        if (choicesRef.current) return;
        try {
            const { base } = await service();
            const response = await fetch(`${base}/health`);
            dispatch({ type: 'event', event: { ...(await response.json()), type: 'hello' } });
        } catch {
            // the service is not running: starting says so
        }
    }, [service]);

    // --- the Designer --------------------------------------------------------------------------
    const load = React.useCallback((ucon: string, style: string | null, source: IScriptPair['source'],
        floorplan?: unknown, revision?: number): boolean => {
        const unity = UnityHandler.instance;
        if (!unity) return false;
        unity.SendMessage('WebInterface', 'SetUConScript', ucon);
        if (style) {
            unity.SendMessage('WebInterface', 'SetStyleScript', style);
            // As UnityView does: the cabinetResult is stale until Unity answers (UCOrderReceived).
            cart.notifyStyleScriptSent();
        }
        setCurrent({ ucon, style, source, ...(floorplan ? { floorplan } : {}), ...(revision ? { revision } : {}) });
        return true;
    }, [cart]);

    const applyDesign = React.useCallback(() => {
        const design = state.design;
        if (!design?.ucon.text || !design.style.text) return;
        load(design.ucon.text, design.style.text, 'ai', undefined, state.agentRevision ?? undefined);
    }, [state.design, state.agentRevision, load]);

    const applyScripts = React.useCallback((ucon: string, style: string | null) => load(ucon, style, 'manual'), [load]);

    // A design opened with the page (a conversation or a room from a link, the last conversation):
    // it goes into the Designer once the page has loaded the product - UnityView sends the product's
    // ucon when Unity is ready, its StyleScript when the parameters arrive - and those have been
    // quiet for a moment, so the product does not overwrite it.
    const pendingDesign = React.useRef<{
        ucon: string; style: string; revision?: number; source: IScriptPair['source'];
    } | null>(null);
    const [pendingTick, setPendingTick] = React.useState(0);
    const loadWhenReady = React.useCallback((ucon: string, style: string, revision?: number,
        source: IScriptPair['source'] = 'revision') => {
        pendingDesign.current = { ucon, style, revision, source };
        setPendingTick((t) => t + 1);
    }, []);
    React.useEffect(() => {
        const pending = pendingDesign.current;
        if (!pending || !loaded) return undefined;
        const timer = window.setTimeout(() => {
            if (pendingDesign.current === pending && load(pending.ucon, pending.style, pending.source, undefined, pending.revision)) {
                pendingDesign.current = null;
            }
        }, 1500);
        return () => window.clearTimeout(timer);
    }, [pendingTick, loaded, data.uconScript, params.parameters, load]);

    // The pair the Designer showed on another page of this tab (the admin Designer, the Designer)
    // opens with this one, unless its link names a conversation or a room; the last conversation
    // still goes on, without loading its own design over it.
    React.useEffect(() => {
        const kept = shownAtStart.current;
        const query = new URLSearchParams(window.location.search);
        if (!kept || query.has('conversation') || query.has('room')) return;
        loadWhenReady(kept.ucon, kept.style, kept.revision, kept.source ?? 'manual');
        keptOver.current = lastAtStart.current;
    }, [loadWhenReady]);

    // What the Designer shows, for the tab's other Designer pages (ShownDesign); not before the
    // design opened with the page is in, the product's pair shows until then.
    React.useEffect(() => {
        if (!current || pendingDesign.current) return;
        keepShownDesign(current.source === 'product' || !current.style ? null : {
            ucon: current.ucon, style: current.style, source: current.source,
            ...(current.revision ? { revision: current.revision } : {}),
        });
    }, [current]);

    const restoreProduct = React.useCallback(() => {
        if (!data.uconScript) return false;
        return load(JSON.stringify(data.uconScript, null, 2), productStyle(params), 'product');
    }, [data.uconScript, params, load]);

    // --- the room ------------------------------------------------------------------------------
    const refreshRoom = React.useCallback(async (room: string) => {
        const [meta, list] = await Promise.all([
            call<{ room: IRoom }>(`/rooms/${room}`),
            call<{ revisions: IRevision[] }>(`/rooms/${room}/revisions`),
        ]);
        if (meta || list) {
            dispatch({ type: 'room', room, label: meta?.room.label ?? undefined, revisions: list?.revisions ?? undefined });
        }
    }, [call]);

    /** Revision n of the room into the Designer; `whenReady`: after the page's own product load (an
     * opened conversation or room). */
    const showRevision = React.useCallback(async (n: number, whenReady = false) => {
        const room = stateRef.current.room;
        if (!room) return false;
        const answer = await call<{ revision: IRevision & { ucon: string; style: string } }>(`/rooms/${room}/revisions/${n}`);
        if (!answer) return false;
        if (whenReady) {
            loadWhenReady(answer.revision.ucon, answer.revision.style, n);
            return true;
        }
        return load(answer.revision.ucon, answer.revision.style, 'revision', undefined, n);
    }, [call, load, loadWhenReady]);

    // The room's revisions, also those made elsewhere (the AR app, another admin), over /rooms/ws.
    React.useEffect(() => {
        const room = state.room;
        if (!room) return undefined;
        refreshRoom(room);
        let closed = false;
        let channel: WebSocket | null = null;
        let timer: number | null = null;
        const open = async () => {
            try {
                const { base, token } = await service();
                if (closed) return;
                channel = new WebSocket(withToken(`${base.replace(/^http/, 'ws')}/rooms/ws`, token));
                channel.onopen = () => channel?.send(JSON.stringify({ type: 'subscribe', room }));
                channel.onmessage = (event) => {
                    try {
                        const message = JSON.parse(String(event.data));
                        if (message?.type === 'revision' && message.room === room) refreshRoom(room);
                    } catch {
                        // not JSON: nothing to do
                    }
                };
                channel.onclose = () => { if (!closed) timer = window.setTimeout(open, 5000); };
            } catch {
                if (!closed) timer = window.setTimeout(open, 15000);
            }
        };
        open();
        return () => {
            closed = true;
            if (timer !== null) window.clearTimeout(timer);
            channel?.close();
        };
    }, [state.room, refreshRoom, service]);

    // An opened conversation, its replay over: its last design (or the room's latest) into the Designer.
    React.useEffect(() => {
        if (!replayPending.current || state.replaying > 0 || !loaded) return;
        replayPending.current = false;
        if (!reopened.current) return;
        reopened.current = false;
        const keptOverIt = keptOver.current !== null && keptOver.current === state.conversation;
        keptOver.current = null;
        if (state.busy || keptOverIt) return;     // the agent's next verify loads it; or the kept pair stays
        if (state.agentRevision) showRevision(state.agentRevision, true);
        else if (state.design?.ok && state.design.ucon.text && state.design.style.text) {
            loadWhenReady(state.design.ucon.text, state.design.style.text);
        }
    }, [state.replaying, state.lastSeq, loaded, state.busy, state.agentRevision, state.design, showRevision, loadWhenReady]);

    // --- the admin's actions -------------------------------------------------------------------
    const openConversation = React.useCallback((id: string) => {
        disconnect();
        answers.current = [];
        reopened.current = true;
        dispatch({ type: 'open', conversation: id });
        connect({ conversation: id, after: 0 }).catch((error) => {
            remember(null);
            dispatch({ type: 'notice', level: 'error', idle: true,
                text: `${error instanceof Error ? error.message : String(error)} Új tervezés indul a következő üzenettel.` });
            dispatch({ type: 'reset', keepRoom: true });
        });
    }, [disconnect, connect]);

    const openRoom = React.useCallback((room: string) => {
        disconnect();
        dispatch({ type: 'reset', keepRoom: false });
        dispatch({ type: 'room', room });
        call<{ room: IRoom & { latest: (IRevision & { ucon: string; style: string }) | null } }>(`/rooms/${room}`)
            .then((answer) => {
                const latest = answer?.room.latest;
                if (latest) loadWhenReady(latest.ucon, latest.style, latest.n);
            });
    }, [disconnect, call, loadWhenReady]);

    const leaveRoom = React.useCallback(() => dispatch({ type: 'room', room: null }), []);

    const saveRevision = React.useCallback(async (note?: string) => {
        const room = stateRef.current.room;
        const shown = currentRef.current;
        if (!room || !shown?.style) return false;
        const answer = await call<{ revision: IRevision }>(`/rooms/${room}/revisions`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ucon: shown.ucon, style: shown.style, note: note ?? 'Kézi módosítás', kind: 'final' }),
        });
        if (!answer) return false;
        setCurrent({ ...shown, source: 'revision', revision: answer.revision.n });
        refreshRoom(room);
        return true;
    }, [call, refreshRoom]);

    const listConversations = React.useCallback(async (room?: string) => {
        const answer = await call<{ conversations: IConversation[] }>(room ? `/rooms/${room}/conversations` : '/conversations');
        return answer?.conversations ?? null;
    }, [call]);

    const listRooms = React.useCallback(async () => (await call<{ rooms: IRoom[] }>('/rooms'))?.rooms ?? null, [call]);

    const renameRoom = React.useCallback(async (label: string) => {
        const room = stateRef.current.room;
        if (!room) return false;
        const answer = await call<{ room: IRoom }>(`/rooms/${room}/label`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label }),
        });
        if (answer) dispatch({ type: 'room', room, label: answer.room.label });
        return !!answer;
    }, [call]);

    // The design shown, for the agent to build on: the scripts, and which revision of the room they are.
    const shownContext = (shown: IScriptPair) => ({
        ucon: shown.ucon, style: shown.style,
        ...(shown.revision ? { revision: shown.revision } : {}),
        ...(shown.source === 'scan' && shown.floorplan ? { floorplan: shown.floorplan } : {}),
    });

    const start = React.useCallback(async (prompt: string, images: IImageInput[], includeCurrent: boolean,
        options: IRunOptions = {}) => {
        dispatch({ type: 'user', text: prompt, images: images.map((i) => i.preview), fresh: true });
        const shown = currentRef.current;
        const room = stateRef.current.room;
        const context = {
            ...(includeCurrent && shown ? { productId: PrestaGlobals.productId, ...shownContext(shown) } : {}),
            ...(room ? { room } : {}),
        };
        await deliver({ type: 'start', prompt, images: plain(images),
            context: Object.keys(context).length ? context : null, ...options });
    }, [deliver]);

    const sendMessage = React.useCallback(async (text: string, images: IImageInput[]) => {
        dispatch({ type: 'user', text, images: images.map((i) => i.preview), fresh: false });
        // the admin stepped back to another revision, or edited the scripts: the agent builds on that
        const shown = currentRef.current;
        const moved = shown && shown.style && (shown.source === 'manual'
            || (shown.source === 'revision' && shown.revision !== stateRef.current.agentRevision));
        await deliver({ type: 'message', text, images: plain(images), ...(moved ? { context: shownContext(shown!) } : {}) });
    }, [deliver]);

    const stop = React.useCallback(() => {
        send(JSON.stringify({ type: 'stop' }));
    }, [send]);

    const takeBack = React.useCallback(() => {
        retry.current.attempt = 0;
        connect().catch((error) => dispatch({ type: 'notice', level: 'error', idle: true,
            text: error instanceof Error ? error.message : String(error) }));
    }, [connect]);

    const reset = React.useCallback(() => {
        remember(null);
        dispatch({ type: 'reset', keepRoom: true });
    }, []);

    const newRoom = React.useCallback(() => {
        remember(null);
        dispatch({ type: 'reset', keepRoom: false });
    }, []);

    const loadScan = React.useCallback(async (file: File): Promise<TScanResult> => {
        if (file.size > MAX_SCAN_BYTES) {
            return { ok: false, error: `Túl nagy szkennelés: ${file.name} (legfeljebb ${MAX_SCAN_BYTES / 1048576} MB)` };
        }
        let answer: {
            ok: boolean; error?: string; ucon?: string; style?: string; floorplan?: unknown; report?: string[];
            room?: IRoom; revision?: IRevision;
        };
        try {
            const { base, token } = await service();
            const label = file.name.replace(/\.[^.]+$/, '');
            const url = withToken(`${base}/floorplan?room=new&label=${encodeURIComponent(label)}`, token);
            const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'model/vnd.usdz+zip' }, body: file });
            answer = await response.json();
        } catch {
            return { ok: false, error: 'Az AI tervező szolgáltatás nem válaszol (fut a designer-agent?).' };
        }
        if (!answer.ok || !answer.ucon || !answer.style) {
            return { ok: false, error: answer.error || 'A szolgáltatás nem adott szobát.' };
        }
        if (answer.room) {
            if (stateRef.current.conversation) {
                disconnect();
                dispatch({ type: 'reset', keepRoom: false });
            }
            dispatch({ type: 'room', room: answer.room.id, label: answer.room.label,
                revisions: answer.revision ? [answer.revision] : [] });
        }
        if (!load(answer.ucon, answer.style, 'scan', answer.floorplan, answer.revision?.n)) {
            return { ok: false, error: 'A Designer még nem töltődött be - próbálja újra egy pillanat múlva.' };
        }
        return { ok: true, report: answer.report ?? [] };
    }, [service, load, disconnect]);

    // A link opens a conversation (?conversation=) or a room (?room=); else the last conversation
    // this tab had goes on.
    const linked = React.useRef(false);
    React.useEffect(() => {
        if (linked.current || !loaded) return;
        linked.current = true;
        const query = new URLSearchParams(window.location.search);
        const conversation = query.get('conversation');
        const room = query.get('room');
        if (conversation) openConversation(conversation);
        else if (room) openRoom(room);
        else {
            const last = lastAtStart.current;
            if (last) openConversation(last);
        }
    }, [loaded, openConversation, openRoom]);

    // The address bar is the link to what is open: the conversation, else the room (once the page
    // has opened what its own link named).
    React.useEffect(() => {
        if (!linked.current) return;
        const url = new URL(window.location.href);
        url.searchParams.delete('conversation');
        url.searchParams.delete('room');
        if (state.conversation) url.searchParams.set('conversation', state.conversation);
        else if (state.room) url.searchParams.set('room', state.room);
        if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href);
    }, [state.conversation, state.room, loaded]);

    // Each verify became a revision: the list has it, also when the revision channel missed it.
    React.useEffect(() => {
        if (state.agentRevision && state.room && !state.revisions.some((r) => r.n === state.agentRevision)) {
            refreshRoom(state.room);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.agentRevision]);

    const value = React.useMemo(() => ({
        ...state, current, start, loadChoices, send: sendMessage, stop, takeBack, reset, newRoom, applyDesign, applyScripts,
        restoreProduct, loadScan, openConversation, openRoom, leaveRoom, showRevision, saveRevision, listConversations, listRooms,
        renameRoom,
    }), [state, current, start, loadChoices, sendMessage, stop, takeBack, reset, newRoom, applyDesign, applyScripts, restoreProduct,
        loadScan, openConversation, openRoom, leaveRoom, showRevision, saveRevision, listConversations, listRooms, renameRoom]);

    return <AiDesignerContext.Provider value={value}>{children}</AiDesignerContext.Provider>;
};

export default AiDesignerProvider;
